import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { NotificationCategory, UserNotificationPreferenceModel } from '../../models/user-notification-preference.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DxSwitchTypes } from 'devextreme-angular/ui/switch';

@Component({
  selector: 'sp-user-settings-notification-projects',
  standalone: false,
  templateUrl: './user-settings-notification-projects.component.html',
  styleUrl: './user-settings-notification-projects.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserSettingsNotificationProjectsComponent implements OnInit, OnChanges {

  @Input() userPreferences: UserNotificationPreferenceModel;
  @Output() updatePreferences = new EventEmitter<UserNotificationPreferenceModel>();

  formGroup: FormGroup;

  localUserPreferences: UserNotificationPreferenceModel;

  constructor(private fb: FormBuilder) { }

  ngOnInit(): void {
    this.initForm();
    if (this.userPreferences) {
      this.localUserPreferences = this.userPreferences;
      this.updateFormValues();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.userPreferences?.currentValue) {
      this.localUserPreferences = changes.userPreferences.currentValue;
      this.updateFormValues();
    }
  }

  get isEmailDisabled(): boolean {
    return !this.userPreferences?.channelSetting?.email;
  }

  onTaskUpdatesSwitchChanged(e: DxSwitchTypes.ValueChangedEvent): void {
    this.handleSwitchChange(e, NotificationCategory.ProjectTask, 'projectTaskUpdatesSwitch');
  }

  private handleSwitchChange(e: DxSwitchTypes.ValueChangedEvent, category: NotificationCategory, controlName: string): void {
    if (!e.event || !this.localUserPreferences) {
      return;
    }

    // Prevent changes when email channel is disabled
    if (!this.localUserPreferences.channelSetting?.email) {
      this.updateFormValues();
      return;
    }

    const emailValue = this.formGroup.controls[controlName].value;
    const taskSetting = this.findOrCreateTaskSetting(category);

    if (taskSetting) {
      taskSetting.email = emailValue;
      this.updatePreferences.emit(this.localUserPreferences);
    }
  }

  private findOrCreateTaskSetting(category: NotificationCategory) {
    if (!this.localUserPreferences.projectTaskSettings) {
      this.localUserPreferences.projectTaskSettings = [];
    }

    let taskSetting = this.localUserPreferences.projectTaskSettings.find(x => x.notificationCategoryId === category);

    if (!taskSetting) {
      taskSetting = {
        notificationCategoryId: category,
        email: false
      };
      this.localUserPreferences.projectTaskSettings.push(taskSetting);
    }

    return taskSetting;
  }

  private updateFormValues(): void {
    if (!this.formGroup || !this.localUserPreferences) {
      return;
    }

    const controls = this.formGroup.controls;
    const emailDisabled = !this.localUserPreferences.channelSetting?.email;

    const getEmailValue = (category: NotificationCategory): boolean => {
      if (emailDisabled) {
        return false;
      }

      const setting = this.localUserPreferences.projectTaskSettings?.find(x => x.notificationCategoryId === category);

      return setting?.email ?? false;
    };

    const switchControl = controls['projectTaskUpdatesSwitch'];
    switchControl.setValue(getEmailValue(NotificationCategory.ProjectTask), { emitEvent: false });

    if (emailDisabled) {
      switchControl.disable({ emitEvent: false });
    } else {
      switchControl.enable({ emitEvent: false });
    }
  }

  private initForm(): void {
    this.formGroup = this.fb.group({
      projectTaskUpdatesSwitch: [''],
    });
  }
}
